import { useEffect, useState } from 'react';

import { FormState, UseFormWatch } from 'react-hook-form';

import { AssignmentFormValues } from '@/modules/planner/components/modals/assignment-modal/assignment-modal';
import { compareFormValues } from '@/utils/form-state.utils';

interface UseAssignmentFormDirtyParams {
  formState: FormState<AssignmentFormValues>;
  watch: UseFormWatch<AssignmentFormValues>;
}

/**
 * Tracks whether the assignment form has unsaved changes.
 *
 * Create mode is always considered dirty (there's always something to save).
 * Edit mode compares the current values with the initial default values.
 */
export function useAssignmentFormDirty({
  formState,
  watch,
}: UseAssignmentFormDirtyParams): boolean {
  const [formDirty, setFormDirty] = useState(false);

  const allFormValues = watch();

  useEffect(() => {
    if (!formState.defaultValues?.id) {
      // For create mode, always consider form as dirty
      setFormDirty(true);
      return;
    }

    // For edit mode, compare current values with default values
    const isDirty = compareFormValues(
      { id: formState.defaultValues.id, ...allFormValues },
      formState.defaultValues
    );

    setFormDirty(isDirty);
  }, [allFormValues, formState.defaultValues]);

  return formDirty;
}
